import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, LOGO } from "../../lib/site";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        {/* Logo — white capsule over the banner, smoothly morphing to a bare logo when scrolled */}
        <motion.a
          href="#home"
          data-scroll
          data-testid="logo-wordmark"
          initial={false}
          animate={
            scrolled
              ? {
                  backgroundColor: "rgba(255,255,255,0)",
                  boxShadow: "0 0 0 0 rgba(15,23,42,0)",
                  paddingTop: 0,
                  paddingBottom: 0,
                  paddingLeft: 0,
                  paddingRight: 0,
                }
              : {
                  backgroundColor: "rgba(255,255,255,0.95)",
                  boxShadow:
                    "0 10px 25px -5px rgba(15,23,42,0.25), 0 8px 10px -6px rgba(15,23,42,0.15)",
                  paddingTop: 12,
                  paddingBottom: 12,
                  paddingLeft: 20,
                  paddingRight: 20,
                }
          }
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center rounded-2xl"
        >
          <img
            src={LOGO}
            alt="Birds Eye Views — Drone Photography"
            className="h-11 w-auto sm:h-12"
          />
        </motion.a>

        {/* Desktop nav */}
        <ul className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((l) => (
            <li key={l.id}>
              <a
                href={`#${l.id}`}
                data-scroll
                data-testid={`nav-${l.id}`}
                className={`group relative rounded-full px-4 py-2 font-sans text-sm font-medium transition-colors ${
                  scrolled
                    ? "text-slate-700 hover:text-emerald-600"
                    : "text-white/90 hover:text-white drop-shadow"
                }`}
              >
                {l.label}
                <span className="absolute inset-x-4 bottom-1 h-px origin-left scale-x-0 bg-emerald-400 transition-transform duration-300 group-hover:scale-x-100" />
              </a>
            </li>
          ))}
        </ul>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`flex h-10 w-10 items-center justify-center rounded-full border lg:hidden ${
            scrolled ? "border-slate-300 text-slate-900" : "border-white/60 bg-white/20 text-white backdrop-blur-sm"
          }`}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            {NAV_LINKS.map((l) => (
              <li key={l.id}>
                <a
                  href={`#${l.id}`}
                  data-scroll
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => setOpen(false)}
                  className="block px-6 py-4 font-sans text-lg text-slate-800"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
